import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { Language, PlannedMeal, Recipe, RecipeCategory } from '../types';
import { getTranslation, isRTL } from '../utils/translations';
import { buildMenuDescription } from '../utils/menuDescription';
import XIcon from './icons/XIcon';
import CornerFlourish from './icons/CornerFlourish';
import PrinterIcon from './icons/PrinterIcon';
import ShoppingBagIcon from './icons/ShoppingBagIcon';
import GanttChartIcon from './icons/GanttChartIcon';
import EditableMealTitle from './EditableMealTitle';

interface MenuOverviewScreenProps {
    recipes: (PlannedMeal & { recipe: Recipe })[];
    title: string;
    language?: Language;
    isHidden?: boolean;
    onClose: () => void;
    onGatherIngredients: () => void;
    onOpenPrepTimeline: () => void;
    onStartPrep: (recipeId: string) => void;
    completedRecipes?: Set<string>;
    mealCompleted?: boolean;
    canEditTitle?: boolean;
    onTitleChange?: (title: string | null) => void;
}

import { RECIPE_CATEGORY_ORDER } from '../utils/recipeCategories';

// Vintage fine-dining menu palette.
const FRAME_LINE = '#CBA177';
const INK = '#2C241B';
const INK_MUTED = '#6E6152';

const MenuOverviewScreen: React.FC<MenuOverviewScreenProps> = ({
    recipes,
    title,
    language,
    isHidden = false,
    onClose,
    onGatherIngredients,
    onOpenPrepTimeline,
    onStartPrep,
    completedRecipes,
    mealCompleted = false,
    canEditTitle = false,
    onTitleChange,
}) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);

    useEffect(() => {
        const clearPrintMode = () => {
            document.documentElement.classList.remove('mealpai-print-mode');
        };

        window.addEventListener('afterprint', clearPrintMode);
        return () => {
            window.removeEventListener('afterprint', clearPrintMode);
            clearPrintMode();
        };
    }, []);

    const handlePrintMenu = () => {
        document.documentElement.classList.add('mealpai-print-mode');
        window.print();
    };

    const hasCategories = useMemo(
        () => recipes.some((mr) => !!mr.recipe.category && mr.recipe.category !== 'General'),
        [recipes],
    );

    const groupedRecipes = useMemo(() => {
        if (!hasCategories) return null;
        const groups = new Map<RecipeCategory, (PlannedMeal & { recipe: Recipe })[]>();
        recipes.forEach((mr) => {
            const category = mr.recipe.category || 'General';
            if (!groups.has(category)) groups.set(category, []);
            groups.get(category)!.push(mr);
        });
        return RECIPE_CATEGORY_ORDER
            .filter((cat) => groups.has(cat))
            .map((cat) => [cat, groups.get(cat)!] as const);
    }, [recipes, hasCategories]);

    const renderRecipeItem = (mr: PlannedMeal & { recipe: Recipe }) => {
        const description = buildMenuDescription(mr.recipe);
        const isDone = completedRecipes?.has(mr.recipe.id) ?? false;
        return (
            <div key={mr.recipe.id} className="mealpai-print-recipe flex flex-col items-center justify-center text-center mb-8 last:mb-0">
                <h3 className="text-2xl font-bold mb-1" style={{ color: INK }}>
                    {mr.recipe.name}
                </h3>
                {description && (
                    <p className="text-sm italic max-w-md" style={{ color: INK_MUTED }}>
                        {description}
                    </p>
                )}
                {isDone && (
                    <div
                        className="mealpai-no-print mt-3 px-4 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                        style={{ borderColor: FRAME_LINE, color: FRAME_LINE, backgroundColor: 'rgba(203, 161, 119, 0.12)' }}
                    >
                        ✓ {t('recipeDone')}
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => onStartPrep(mr.recipe.id)}
                    style={{ borderColor: FRAME_LINE, color: INK }}
                    className={`mealpai-no-print ${isDone ? 'mt-2' : 'mt-3'} px-4 py-1.5 rounded-full border transition-colors text-xs font-bold uppercase tracking-wider hover:text-[#FDFBF7]`}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = INK; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                    {isDone ? t('viewRecipe') : `${t('startPrep')} ${rtl ? '◀' : '▶'}`}
                </button>
            </div>
        );
    };

    if (isHidden) {
        return null;
    }

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex flex-col bg-stone-200 dark:bg-slate-950 overflow-hidden transition-colors duration-200 mealpai-print-menu"
            dir={rtl ? 'rtl' : 'ltr'}
        >
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none mealpai-print-scroll">
                <div className="px-4 sm:px-6 py-10 mealpai-print-page">
                    <div
                        className="mealpai-print-frame relative w-full max-w-2xl mx-auto bg-[#FDFBF7] border-[3px] p-1.5 shadow-lg"
                        style={{ borderColor: FRAME_LINE }}
                    >
                        <CornerFlourish className="absolute -top-1 -left-1 w-12 h-12 text-[#CBA177]" />
                        <CornerFlourish className="absolute -top-1 -right-1 w-12 h-12 text-[#CBA177] rotate-90" />
                        <CornerFlourish className="absolute -bottom-1 -right-1 w-12 h-12 text-[#CBA177] rotate-180" />
                        <CornerFlourish className="absolute -bottom-1 -left-1 w-12 h-12 text-[#CBA177] -rotate-90" />

                        <div className="mealpai-print-inner relative border p-8 flex flex-col" style={{ borderColor: FRAME_LINE }}>
                            <div
                                className="mealpai-no-print absolute inset-x-4 top-4 flex flex-row items-center justify-between"
                                dir={rtl ? 'rtl' : 'ltr'}
                            >
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="transition-colors hover:opacity-70"
                                    style={{ color: FRAME_LINE }}
                                >
                                    <XIcon className="w-6 h-6" />
                                </button>

                                <div className="flex gap-4" style={{ color: FRAME_LINE }}>
                                    <button
                                        type="button"
                                        onClick={onOpenPrepTimeline}
                                        title={t('prepTimelineTitle')}
                                        aria-label={t('prepTimelineTitle')}
                                        className="transition-colors hover:opacity-70"
                                    >
                                        <GanttChartIcon className="w-6 h-6" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrintMenu}
                                        title={t('printMenu')}
                                        aria-label={t('printMenu')}
                                        className="transition-colors hover:opacity-70"
                                    >
                                        <PrinterIcon className="w-6 h-6" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onGatherIngredients}
                                        title={t('gatherIngredients')}
                                        aria-label={t('gatherIngredients')}
                                        className="transition-colors hover:opacity-70"
                                    >
                                        <ShoppingBagIcon className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="text-3xl font-serif font-bold tracking-tight text-center mt-12 flex justify-center px-4" style={{ color: INK }}>
                                <EditableMealTitle
                                    value={title}
                                    canEdit={canEditTitle && Boolean(onTitleChange)}
                                    onSave={(nextTitle) => onTitleChange?.(nextTitle)}
                                    placeholder={t('mealNamePlaceholder')}
                                    editLabel={t('editMealName')}
                                    className="justify-center text-3xl font-serif font-bold tracking-tight"
                                    inputClassName="w-full max-w-md text-center text-3xl font-serif font-bold tracking-tight bg-white/80 border border-[#CBA177]/40 rounded-lg px-3 py-1"
                                    style={{ color: INK }}
                                />
                            </div>

                            {mealCompleted && (
                                <p className="text-center mt-4 text-sm font-semibold uppercase tracking-widest" style={{ color: FRAME_LINE }}>
                                    ✓ {t('mealCompletedTitle')}
                                </p>
                            )}

                            <div className="mealpai-print-body mt-10">
                                {recipes.length === 0 && (
                                    <p className="text-center font-medium py-16" style={{ color: INK_MUTED }}>
                                        {t('noRecipes')}
                                    </p>
                                )}

                                {groupedRecipes
                                    ? groupedRecipes.map(([category, items]) => (
                                        <div key={category} className="mealpai-print-category">
                                            <div className="flex items-center justify-center gap-4 mb-6 mt-10">
                                                <span className="h-px w-12" style={{ backgroundColor: FRAME_LINE }} />
                                                <h2 className="text-lg font-serif tracking-widest" style={{ color: INK_MUTED }}>
                                                    {t(category.toLowerCase())}
                                                </h2>
                                                <span className="h-px w-12" style={{ backgroundColor: FRAME_LINE }} />
                                            </div>
                                            {items.map(renderRecipeItem)}
                                        </div>
                                    ))
                                    : recipes.map(renderRecipeItem)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default MenuOverviewScreen;
